/**
 * Secret Entity — Domain Types
 *
 * All wire shapes come from the Fern-generated client
 * (`@agentaai/api-client`) so this package stays aligned with the backend
 * OpenAPI definition. The hand-rolled DTOs that used to live here have
 * been removed; consumers should import the Fern names directly.
 *
 * What stays:
 *   - `PROVIDER_LABELS` / `PROVIDER_KINDS` — app-level provider catalog
 *     (rendering, slug normalization). No wire equivalent.
 *   - `STANDARD_PROVIDER_KINDS` — the standard provider list minus the
 *     legacy `"mistralai"` alias (kept in Fern for backwards compat but
 *     not shown in OSS provider pickers).
 *   - `VaultMigrationStatus` — UI state for the one-time localStorage
 *     migration; not a wire shape.
 */

import type {LlmProvider} from "@agenta/shared/types"
import {AgentaApi} from "@agentaai/api-client"

// ---------------------------------------------------------------------------
// Fern type aliases
// ---------------------------------------------------------------------------

export type Header = AgentaApi.Header
export type LegacyLifecycleDto = AgentaApi.LegacyLifecycleDto

export type SecretDto = AgentaApi.SecretDto

export type SecretResponseDto = AgentaApi.PublicSecretResponseDto

export type CreateSecretDto = AgentaApi.CreateSecretDto
export type UpdateSecretDto = AgentaApi.UpdateSecretDto

export type StandardProviderSettingsDto = AgentaApi.StandardProviderSettingsDto
export type CustomProviderSettingsDto = AgentaApi.CustomProviderSettingsDto
export type CustomModelSettingsDto = AgentaApi.CustomModelSettingsDto

/**
 * Connection policy fields not yet represented by the generated client.
 */
export type StandardProviderDto = AgentaApi.StandardProviderDto & {
    models?: CustomModelSettingsDto[] | null
    harnesses?: string[] | null
}
export type McpStandardProviderDto = AgentaApi.McpStandardProviderDto
export type CustomProviderDto = AgentaApi.CustomProviderDto & {
    harnesses?: string[] | null
    protocol?: LlmEndpointProtocol | null
}

/**
 * The wire protocol a custom endpoint speaks, and therefore the provider family it belongs to.
 *
 * Only the family changes behaviour: the relay serves `/v1/chat/completions`, `/v1/responses` and
 * `/v1/messages` on every custom endpoint, and the harness picks the suffix. Absent on records
 * written before the field existed, which the resolver already reads as `openai`.
 */
export const LlmEndpointProtocol = {
    Openai: "openai",
    Anthropic: "anthropic",
} as const
export type LlmEndpointProtocol = (typeof LlmEndpointProtocol)[keyof typeof LlmEndpointProtocol]

export type CustomSecretDto = AgentaApi.CustomSecretDto
export type CustomSecretSettingsDto = AgentaApi.CustomSecretSettingsDto

export const CustomSecretFormat = AgentaApi.CustomSecretFormat
export type CustomSecretFormat = AgentaApi.CustomSecretFormat

export const SecretManagementPolicy = AgentaApi.SecretManagementPolicy
export type SecretManagementPolicy = AgentaApi.SecretManagementPolicy
/**
 * Flat json content for a `json`-format custom secret: a single-level map of
 * primitives. Mirrors the backend's flat-only validation (no nesting/arrays).
 */
export type CustomSecretContent = CustomSecretSettingsDto["content"]

/**
 * Table/form row for a user-named vault secret (`custom_secret`). Extends the
 * shared `LlmProvider` row (so it flows through the generic vault transforms)
 * with the two fields named secrets need: the `format` and a `content` that is
 * a text blob or a flat json object — wider than `LlmProvider.key` (string only).
 */
export interface AgentSecretBinding {
    secret: {slug: string}
    binding: {type: "env"; name: string}
}

/**
 * A vault row carrying the custom-provider fields the generated client does not declare yet.
 *
 * `LlmProvider` lives in `@agenta/shared` and stays free of this domain's wire additions, so the
 * secret entity widens it here — the same way `CustomProviderDto` is widened above.
 */
export interface ProviderVaultRow extends LlmProvider {
    /** Declared endpoint protocol on a `custom_provider` row; absent means the row declares none. */
    protocol?: LlmEndpointProtocol
}

export interface NamedSecretRow extends LlmProvider {
    slug?: string
    format: CustomSecretFormat
    /** Optional environment-variable name suggested when this secret is attached to an agent. */
    defaultEnvVar?: string
    /** Absent on a write-only record (the value never comes back) and on an update that keeps it. */
    content?: CustomSecretContent
}

// `SecretKind` / `StandardProviderKind` / `CustomProviderKind` are Fern
// const-asserted objects. Re-export both the value and the derived type
// so callers can use them like an enum (`SecretKind.ProviderKey`).
export const SecretKind = AgentaApi.SecretKind
export type SecretKind = AgentaApi.SecretKind

// The wire names gained an `Llm` prefix when the gateway split LLM and MCP
// providers into separate enums. The local names stay unprefixed — they are
// this package's public API and ~30 call sites across three packages import
// them from here — so grep the wire spelling when tracing back to Fern.
export const StandardProviderKind = AgentaApi.LlmStandardProviderKind
export type StandardProviderKind = AgentaApi.LlmStandardProviderKind

export const McpStandardProviderKind = AgentaApi.McpStandardProviderKind
export type McpStandardProviderKind = AgentaApi.McpStandardProviderKind

export const CustomProviderKind = AgentaApi.LlmCustomProviderKind
export type CustomProviderKind = AgentaApi.LlmCustomProviderKind

// ---------------------------------------------------------------------------
// App-level catalog (no wire equivalent)
// ---------------------------------------------------------------------------

export const PROVIDER_LABELS: Record<string, string> = {
    openai: "OpenAI",
    cohere: "Cohere",
    anyscale: "Anyscale",
    deepinfra: "DeepInfra",
    alephalpha: "Aleph Alpha",
    groq: "Groq",
    mistral: "Mistral AI",
    mistralai: "Mistral AI",
    anthropic: "Anthropic",
    perplexityai: "Perplexity AI",
    together_ai: "Together AI",
    openrouter: "OpenRouter",
    gemini: "Google Gemini",
    vertex_ai: "Google Vertex AI",
    bedrock: "AWS Bedrock",
    sagemaker: "AWS SageMaker",
    azure: "Azure OpenAI",
    minimax: "MiniMax",
    xai: "xAI",
    // Stored value stays "custom"; only the user-visible label changes. The v1 custom deployment
    // speaks the OpenAI Chat Completions dialect, so name it for what it connects to.
    custom: "OpenAI-compatible endpoint",
}

export const PROVIDER_KINDS: Record<string, string> = {
    ...Object.entries(PROVIDER_LABELS).reduce(
        (acc, [kind, label]) => {
            acc[kind] = kind
            acc[label.toLowerCase()] = kind
            return acc
        },
        {} as Record<string, string>,
    ),
    // Normalize legacy "mistralai" slug to canonical "mistral"
    mistralai: "mistral",
}

/**
 * Standard provider kinds shown in the OSS provider picker.
 *
 * Fern includes both `"mistral"` and `"mistralai"` in `StandardProviderKind`
 * for backwards compatibility, but the OSS LLM picker only shows the canonical
 * `"mistral"` entry — filter the alias out here.
 *
 * `"mock"` is filtered for a different reason: it is the gateway's test double,
 * not a provider anyone can hold credentials for, so it has no place in a
 * user-facing picker. It is a wire kind, so it stays in the enum and in
 * `LITELLM_MODEL_PREFIXES`; it is only hidden from this list.
 */
export const STANDARD_PROVIDER_KINDS: StandardProviderKind[] = (
    Object.values(StandardProviderKind) as StandardProviderKind[]
).filter((kind) => kind !== StandardProviderKind.Mistralai && kind !== StandardProviderKind.Mock)

/**
 * Truthy, obviously-not-a-key sentinel the vault persister writes to disk in place of secret
 * values. It lives here rather than beside the persister because readers of a restored row — the
 * connection card seeds its credential fields from one — must recognise it as "no value yet".
 */
export const VAULT_PERSIST_REDACTED = "[redacted]"

/**
 * Every `LlmProvider` field that can carry actual secret material — the fields the vault strips
 * from a write-only response, and the fields the IndexedDB persister replaces with a sentinel.
 * One list so the two can never disagree about what counts as a secret.
 */
export const SECRET_VALUE_FIELDS = [
    "key",
    "apiKey",
    "accessKeyId",
    "accessKey",
    "sessionToken",
    "bearerToken",
    "vertexCredentials",
] as const

// ---------------------------------------------------------------------------
// Migration status (UI state, not wire)
// ---------------------------------------------------------------------------

/**
 * Migration status for the legacy localStorage → vault migration.
 *
 * `migrating: true` while migration is in flight; `migrated: true` after success.
 * On logout the hook resets both to `false` so that the next sign-in re-arms
 * the migration if needed.
 */
export interface VaultMigrationStatus {
    migrating: boolean
    migrated: boolean
}
